import React, { useState } from 'react';
import { X, User, Phone, Mail, MapPin, Lock, CreditCard, Calendar } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { quickAdd } from '../services/api';
import { toast } from 'sonner';
import PetIcon from './PetIcon';

const validatePhone = (p) => /^05\d{9}$/.test(p);

const SPECIES = [
  { v: 'dog', l: 'Köpek' },
  { v: 'cat', l: 'Kedi' },
  { v: 'bird', l: 'Kuş' },
  { v: 'other', l: 'Diğer' },
];

const GENDERS = [
  { v: 'male', l: 'Erkek' },
  { v: 'female', l: 'Dişi' },
];

const INIT = {
  name: '', phone: '', email: '', address: '', password: '', tc_kimlik_no: '', birth_date: '',
  pet_name: '', pet_species: 'dog', pet_breed: '', pet_gender: '', pet_color: '', pet_birth_date: '', pet_weight_grams: '', pet_notes: '',
};

const QuickCustomerModal = ({ open, onClose, prefillName, prefillPhone }) => {
  const [form, setForm] = useState({ ...INIT });
  const [saving, setSaving] = useState(false);
  const [withPet, setWithPet] = useState(false);

  const reset = () => { setForm({ ...INIT }); setWithPet(false); };
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  React.useEffect(() => {
    if (open) {
      const f = { ...INIT };
      if (prefillName) f.name = prefillName;
      if (prefillPhone) f.phone = prefillPhone;
      setForm(f);
    }
  }, [open, prefillName, prefillPhone]);

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast.error('Müşteri adı gerekli'); return; }
    if (!validatePhone(form.phone)) { toast.error('Telefon: 05 ile başlamalı, 11 haneli'); return; }
    if (withPet && !form.pet_name.trim()) { toast.error('Hasta adı gerekli'); return; }
    setSaving(true);
    try {
      const payload = {
        customer_name: form.name.trim(),
        customer_phone: form.phone,
        customer_email: form.email.trim() || null,
        customer_address: form.address.trim() || null,
        customer_password: form.password || '',
        customer_tc_kimlik_no: form.tc_kimlik_no.trim() || null,
        customer_birth_date: form.birth_date || null,
        pet_name: withPet ? form.pet_name.trim() : '',
        pet_species: withPet ? form.pet_species : 'dog',
        pet_breed: withPet ? (form.pet_breed.trim() || null) : null,
        pet_gender: withPet ? (form.pet_gender || null) : null,
        pet_color: withPet ? (form.pet_color.trim() || null) : null,
        pet_birth_date: withPet ? (form.pet_birth_date || null) : null,
        pet_weight_grams: withPet && form.pet_weight_grams ? parseInt(form.pet_weight_grams) : null,
        pet_notes: withPet ? (form.pet_notes.trim() || null) : null,
      };
      await quickAdd(payload);
      toast.success(withPet && form.pet_name ? 'Müşteri ve hasta oluşturuldu' : 'Müşteri oluşturuldu');
      reset();
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Bir hata oluştu');
    }
    setSaving(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-x-0 top-0 bottom-[65px] z-[9998] flex sm:inset-0 sm:items-center sm:justify-center sm:p-3" data-testid="quick-customer-modal">
      <div className="absolute inset-0 bg-black/40" onClick={() => { reset(); onClose(false); }} />
      <div className="relative w-full sm:max-w-md lg:max-w-3xl bg-white sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[75vh] shadow-2xl">
        <div className="px-4 lg:px-6 pt-4 pb-3 flex items-center justify-between border-b border-gray-100 shrink-0">
          <h2 className="text-base lg:text-lg font-bold text-gray-900">Yeni Müşteri</h2>
          <button onClick={() => { reset(); onClose(false); }} className="p-1.5 rounded-full hover:bg-gray-100">
            <X size={22} className="text-[#8E8E93]" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 lg:p-6 space-y-3">
          {/* Müşteri Bilgileri */}
          <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Müşteri Bilgileri</p>

          <div>
            <Label className="text-xs">Ad Soyad *</Label>
            <div className="relative mt-1">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
              <Input placeholder="Müşteri adı" value={form.name}
                onChange={e => set('name', e.target.value)}
                className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" autoFocus data-testid="customer-name-input" />
            </div>
          </div>

          <div>
            <Label className="text-xs">Telefon *</Label>
            <div className="relative mt-1">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={16} />
              <Input type="tel" inputMode="numeric" maxLength={11} placeholder="05XXXXXXXXX" value={form.phone}
                onChange={e => set('phone', e.target.value.replace(/\D/g, '').slice(0, 11))}
                className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-phone-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">E-posta</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
                <Input type="email" placeholder="Opsiyonel" value={form.email}
                  onChange={e => set('email', e.target.value)}
                  className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-email-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Adres</Label>
              <div className="relative mt-1">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
                <Input placeholder="Opsiyonel" value={form.address}
                  onChange={e => set('address', e.target.value)}
                  className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-address-input" />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div>
              <Label className="text-xs">TC Kimlik No</Label>
              <div className="relative mt-1">
                <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
                <Input inputMode="numeric" maxLength={11} placeholder="Opsiyonel" value={form.tc_kimlik_no}
                  onChange={e => set('tc_kimlik_no', e.target.value.replace(/\D/g, '').slice(0, 11))}
                  className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-tc-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{`Doğum Tarihi`}</Label>
              <div className="relative mt-1">
                <Input type="date" placeholder="Opsiyonel" value={form.birth_date}
                  onChange={e => set('birth_date', e.target.value)}
                  className="h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-birthdate-input" />
              </div>
            </div>
          </div>

          <div>
            <Label className="text-xs">{`Şifre (Müşteri Girişi)`}</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8E8E93]" size={14} />
              <Input type="password" placeholder="Opsiyonel" value={form.password}
                onChange={e => set('password', e.target.value)}
                className="pl-9 h-10 text-sm bg-[#F2F2F7] border-0 rounded-xl" data-testid="customer-password-input" />
            </div>
          </div>

          {/* Hasta Ekleme */}
          {!withPet ? (
            <button onClick={() => setWithPet(true)}
              className="w-full p-3 border-2 border-dashed border-[#C7C7CC] rounded-xl flex items-center justify-center gap-2 text-[#6b6de3] font-medium text-sm"
              data-testid="add-pet-toggle">
              + Hasta da ekle
            </button>
          ) : (
            <div className="bg-blue-50/50 rounded-xl p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-[#8E8E93] uppercase tracking-wider">Hasta Bilgileri</p>
                <button onClick={() => setWithPet(false)} className="text-xs text-[#FF3B30] font-medium">Kaldır</button>
              </div>

              <div>
                <Label className="text-xs">Hasta Adı *</Label>
                <Input placeholder="Hasta adı" value={form.pet_name}
                  onChange={e => set('pet_name', e.target.value)}
                  className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-name-input" />
              </div>

              <div>
                <Label className="text-xs">Tür *</Label>
                <div className="flex gap-1.5 mt-1">
                  {SPECIES.map(s => (
                    <button key={s.v} onClick={() => set('pet_species', s.v)}
                      className={`flex-1 py-2 rounded-xl text-xs font-medium flex items-center justify-center gap-1 transition-colors ${
                        form.pet_species === s.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'
                      }`}>
                      <PetIcon species={s.v} size={13} /> {s.l}
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-xs">Irk</Label>
                  <Input placeholder="Opsiyonel" value={form.pet_breed}
                    onChange={e => set('pet_breed', e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-breed-input" />
                </div>
                <div>
                  <Label className="text-xs">Cinsiyet</Label>
                  <div className="flex gap-1.5 mt-1">
                    {GENDERS.map(g => (
                      <button key={g.v} onClick={() => set('pet_gender', form.pet_gender === g.v ? '' : g.v)}
                        className={`flex-1 h-10 rounded-xl text-xs font-medium transition-colors ${
                          form.pet_gender === g.v ? 'bg-[#6b6de3] text-white' : 'bg-white text-gray-700'
                        }`}>{g.l}</button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs">Renk</Label>
                  <Input placeholder="Opsiyonel" value={form.pet_color}
                    onChange={e => set('pet_color', e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-color-input" />
                </div>
                <div>
                  <Label className="text-xs">Doğum Tarihi</Label>
                  <Input type="date" value={form.pet_birth_date}
                    onChange={e => set('pet_birth_date', e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-birthdate-input" />
                </div>
                <div>
                  <Label className="text-xs">Ağırlık (gram)</Label>
                  <Input type="number" placeholder="Ör: 3500" value={form.pet_weight_grams}
                    onChange={e => set('pet_weight_grams', e.target.value)}
                    className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-weight-input" />
                  {form.pet_weight_grams && parseInt(form.pet_weight_grams) >= 1000 && (
                    <p className="text-[10px] text-[#8E8E93] mt-0.5">{(parseInt(form.pet_weight_grams) / 1000).toFixed(2)} kg</p>
                  )}
                </div>
              </div>

              <div>
                <Label className="text-xs">Notlar</Label>
                <Input placeholder="Opsiyonel" value={form.pet_notes}
                  onChange={e => set('pet_notes', e.target.value)}
                  className="h-10 text-sm bg-white border-0 rounded-xl mt-1" data-testid="pet-notes-input" />
              </div>
            </div>
          )}
        </div>

        <div className="p-4 lg:p-6 border-t border-gray-100 shrink-0">
          <Button onClick={handleSubmit} disabled={saving}
            className="w-full h-11 bg-[#6b6de3] hover:bg-[#5658c4] rounded-xl text-sm font-semibold"
            data-testid="save-customer-btn">
            {saving ? 'Kaydediliyor...' : 'Kaydet'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default QuickCustomerModal;
